import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import type { CanvasCapture } from '@repo/contracts';

import { trackEvent } from '../analytics/posthog';
import {
  canvasKeys,
  createCapture,
  type CreateCapturePayload,
  deleteCapture,
  listSessionCaptures,
  moveCapture,
  uploadCaptureImage,
} from '../api/canvas-captures';
import { logger } from '../lib/logger';
import { usePollWindow } from '../lib/use-poll-window';

const log = logger.child({ module: 'captures' });

/**
 * Everything on one session's board, plus the three ways it changes.
 *
 * Deletes and moves are optimistic, because both are direct manipulation: a
 * card that lingers after "delete", or snaps back to its old spot before
 * settling, reads as the app fighting the user. Creates are not — a capture
 * takes a real round trip to upload megabytes of PNG, and inventing a
 * placeholder card would mean guessing an id, a signed URL and a position, then
 * reconciling all three.
 */
export function useCaptures(
  sessionId: string | null,
  /** Resolves the session to file a capture under, creating it on the first one. */
  ensureSession?: () => Promise<string>,
  /**
   * Called with the first capture of a session.
   *
   * The whole row rather than its id: a walk leaves for the board from
   * here, and the session it is leaving for is on the capture. This hook's own
   * `sessionId` is still null at this point on the first capture of a new session —
   * the capture is what created it.
   */
  onFirstCapture?: (capture: CanvasCapture) => void,
) {
  const queryClient = useQueryClient();
  const key = canvasKeys.sessionCaptures(sessionId ?? 'none');

  const cached = queryClient.getQueryData<CanvasCapture[]>(key);

  /**
   * Screens are named by a model a few seconds after the capture is stored, so
   * the row this client already holds is correct but incomplete. Poll until
   * every capture has a name, then stop — otherwise the board says
   * "Capture 1" until someone reloads.
   *
   * A plain value rather than the predicate form of `refetchInterval`: the
   * predicate is evaluated against query state that has not always caught up
   * with what was just written, and polling silently stopped after the first
   * couple of ticks.
   */
  const awaitingNames = (cached ?? []).some((capture) => !capture.name);
  // Bounded, because a name whose job died is never coming. See usePollWindow.
  const namePoll = usePollWindow(awaitingNames);

  const query = useQuery({
    // Disabled until the session exists — there is no board to load before then.
    enabled: Boolean(sessionId),
    queryFn: () => listSessionCaptures(sessionId!),
    queryKey: key,
    refetchInterval: namePoll.refetchInterval,
    // The board is only changed by this client, through the mutations below,
    // so a background refetch can only replace correct data with the same data
    // — or worse, with a stale copy mid-drag.
    staleTime: Infinity,
  });

  const create = useMutation({
    // Two steps in one mutation, because they are one action to the user and
    // there is nothing useful to do between them: upload the PNG straight to
    // the bucket, then record the row that points at it.
    mutationFn: async ({
      image,
      ...payload
    }: Omit<CreateCapturePayload, 'sessionId' | 'storagePath'> & {
      image: Blob | string;
    }) => {
      // The session is created here, by the first capture — see useSession. Resolved
      // before the upload so a failure to start one costs nothing but a retry.
      const targetSession = sessionId ?? (await ensureSession?.());
      if (!targetSession) throw new Error('No session to capture into');

      const storagePath = await uploadCaptureImage(image);
      return createCapture({
        ...payload,
        sessionId: targetSession,
        storagePath,
      });
    },
    onError: (err) => {
      log.error({ err }, 'capture upload failed');
      trackEvent('capture_failed', { reason: 'upload', source: 'browser' });
    },
    onSuccess: (capture) => {
      trackEvent('capture_taken', {
        device: capture.device,
        full_page: capture.fullPage,
        source: 'browser',
      });
      // Keyed by the capture's own session, not this hook's — the first capture
      // creates the session, so this hook's key only becomes correct after it lands.
      const key = canvasKeys.sessionCaptures(capture.sessionId);
      const before = queryClient.getQueryData<CanvasCapture[]>(key) ?? [];
      queryClient.setQueryData<CanvasCapture[]>(key, [...before, capture]);
      // Home's cards read their screenshot from the site's captures, cached
      // forever — without this, going back shows the new room with no
      // thumbnail until a reload.
      void queryClient.invalidateQueries({
        queryKey: canvasKeys.projectCaptures(capture.projectId),
      });

      if (before.length === 0) onFirstCapture?.(capture);
    },
  });

  const move = useMutation({
    mutationFn: ({ id, x, y }: { id: string; x: number; y: number }) =>
      moveCapture(id, x, y),
    // onMutate before onError: TanStack infers the rollback context from this
    // return type, and TS reads the object literal in order — declared after,
    // `context` types as {} and the rollback silently doesn't compile.
    onMutate: ({ id, x, y }) => {
      const previous = queryClient.getQueryData<CanvasCapture[]>(key);
      queryClient.setQueryData<CanvasCapture[]>(key, (prev) =>
        (prev ?? []).map((capture) =>
          capture.id === id ? { ...capture, x, y } : capture,
        ),
      );
      return { previous };
    },
    onError: (err, _variables, context) => {
      log.error({ err }, 'capture move failed');
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteCapture(id),
    onMutate: (id) => {
      const previous = queryClient.getQueryData<CanvasCapture[]>(key);
      queryClient.setQueryData<CanvasCapture[]>(key, (prev) =>
        (prev ?? []).filter((capture) => capture.id !== id),
      );
      return { previous };
    },
    onError: (err, _id, context) => {
      log.error({ err }, 'capture delete failed');
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
  });

  /**
   * Stores one capture, awaited — the walk may only leave for the board once
   * its page is safely stored, since navigating away unmounts the session.
   */
  const saveCaptureAsync = useCallback(
    (
      payload: Omit<CreateCapturePayload, 'sessionId' | 'storagePath'> & {
        image: Blob | string;
      },
    ) => create.mutateAsync(payload),
    [create],
  );

  const moveCard = useCallback(
    (id: string, x: number, y: number) => {
      move.mutate({ id, x, y });
    },
    [move],
  );

  const removeCard = useCallback(
    (id: string) => {
      remove.mutate(id);
    },
    [remove],
  );

  return {
    captures: query.data ?? [],
    /** True while a PNG is uploading — the capture already exists on the
     *  worker, so this is "saving", not "capturing". */
    isSaving: create.isPending,
    loading: query.isLoading,
    moveCard,
    removeCard,
    saveCaptureAsync,
    saveError: create.error,
  };
}
